import { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Paper, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { autorun } from 'mobx';

import { KostenPunkt } from '@codefluegel/wowis-rest-typescript-client';
import GridContainer from '../components/container/GridContainer';

import BarChart from '../components/charts/BarChart';
import { useStore } from '../store/context';
import { useKostenApi } from '../ApiClient';
import DoughnutChart from '../components/charts/DoughnutChart';

const AnnualBillingPage = observer((): JSX.Element => {
  const { welcomeStore } = useStore();
  const { currentEstate } = welcomeStore;

  const [costs, setCosts] = useState<KostenPunkt[]>();
  const [selectedYear, setSelectedYear] = useState('');

  const kostenApi = useKostenApi();

  const theme = useTheme();

  useEffect(
    () =>
      autorun(() => {
        async function fetchKostenData() {
          try {
            if (!currentEstate?.customerId) return;
            const { data } = await kostenApi.getAllKosten(
              currentEstate?.customerId,
            );
            const initialYear = data
              ?.map(cost => cost.jahr.toString())
              .sort()
              .reverse()[0];

            setSelectedYear(initialYear || '');
            setCosts(data);
          } catch (error) {
            // TODO: add error handling
            console.log(error);
          }
        }
        fetchKostenData();
      }),
    [kostenApi, setSelectedYear, currentEstate?.customerId],
  );

  function filterByYear(data: KostenPunkt[] | undefined, year: string) {
    return data?.filter(cost => cost.jahr.toString() === year);
  }

  // reduce sum  of values
  const sumOfValues = (costArray: number[]) =>
    costArray.reduce((a, b) => a + b, 0);

  function getSumValuesByBez(operatingCosts: KostenPunkt[] | undefined): {
    [key: string]: number;
  } {
    const sumValuesByBez: { [key: string]: number } = {};
    operatingCosts?.forEach(cost => {
      if (!cost.bez) return;
      if (sumValuesByBez[cost.bez]) {
        sumValuesByBez[cost.bez] += cost.betrag;
      } else {
        sumValuesByBez[cost.bez] = cost.betrag;
      }
    });
    return sumValuesByBez;
  }

  const sumValuesByBez = getSumValuesByBez(filterByYear(costs, selectedYear));
  const labels = Object.keys(sumValuesByBez);
  // Memoize random colors for consistency across renders
  const randomColor = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return `rgb(${r},${g},${b})`;
  };

  const backgroundColors = useMemo(
    () => labels.map(() => randomColor()),
    [costs],
  );
  const values = Object.values(sumValuesByBez);

  // get all years from costs
  const allYears = costs
    ?.map(cost => cost.jahr.toString())
    .sort()
    .reverse();
  const availableYears = allYears?.filter(
    (element, index) => allYears?.indexOf(element) === index,
  );

  const paperStyle = {
    backgroundColor: theme.palette.secondary.light, // Change this to the color you prefer
  };

  return (
    <Box>
      <GridContainer>
        {costs && costs?.length > 0 ? (
          <Grid container spacing={1} sx={{ marginTop: '20px' }}>
            <Grid item xs={12} md={6} bgcolor={theme.palette.secondary.light}>
              <Paper elevation={0} sx={{ p: 4 }} style={paperStyle}>
                <DoughnutChart
                  labels={labels}
                  values={values}
                  backgroundColors={backgroundColors}
                  allYears={availableYears}
                  sum={sumOfValues(values)}
                  selectedYear={selectedYear}
                  setSelectedYear={setSelectedYear}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} bgcolor={theme.palette.secondary.light}>
              <Paper elevation={0} sx={{ p: 4 }} style={paperStyle}>
                <BarChart
                  allYears={availableYears || ['']}
                  data={costs}
                  backgroundColors={backgroundColors}
                />
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={1}
            sx={{ marginTop: '20px', height: 200, width: '100%' }}
          >
            <Grid item xs={12} md={6} bgcolor={theme.palette.secondary.light}>
              Keine Daten derzeit verfügbar
            </Grid>
          </Grid>
        )}
      </GridContainer>
    </Box>
  );
});

export default AnnualBillingPage;
